import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { UserAvatar } from '@/components/UserAvatar';
import { useAuth } from '@/context/AuthContext';
import type { UserSideBetPick } from '@/lib/supabase-queries';
import { isSideBetCorrect } from '@/lib/side-bets-display';

interface MyBetsSummaryProps {
  bets: any[];
  seriesList: any[];
  groupId: string;
  sideBetPicks?: UserSideBetPick[];
  leaderboardTotal?: number | null;
}

export function MyBetsSummary({
  bets,
  seriesList,
  groupId,
  sideBetPicks = [],
  leaderboardTotal,
}: MyBetsSummaryProps) {
  const { avatarId, displayName } = useAuth();

  // Build series lookup by id
  const seriesById: Record<string, any> = {};
  seriesList.forEach((s) => { seriesById[s.id] = s; });

  // Counter = placed series bets + picked side bets
  const totalActivities = bets.length + sideBetPicks.length;

  // Completed / resolved accuracy across both bet types
  const completedBets = bets.filter((b) => {
    const series = seriesById[b.series_id];
    return series?.status === 'completed';
  });
  const correctWinners = completedBets.filter((b) => b.is_correct_winner === true).length;
  const exactScores = bets.filter((b) => b.is_correct_score === true).length;

  const resolvedPicks = sideBetPicks.filter((p) => p.status === 'resolved');
  const correctSideBets = resolvedPicks.filter(isSideBetCorrect).length;

  const totalCompleted = completedBets.length + resolvedPicks.length;
  const totalCorrect = correctWinners + correctSideBets;

  // Prefer authoritative leaderboard total (includes side bet + champion bonus)
  const seriesPoints = bets.reduce((sum, b) => sum + (b.points_earned || 0), 0);
  const sideBetPoints = sideBetPicks.reduce((sum, p) => sum + (p.points_earned || 0), 0);
  const totalPoints =
    typeof leaderboardTotal === 'number' ? leaderboardTotal : seriesPoints + sideBetPoints;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      {/* Section header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight">ההימורים שלי</h2>
        <Link
          to={`/group/${groupId}/bets`}
          className="text-sm text-accent hover:underline"
        >
          כל ההימורים ←
        </Link>
      </div>

      {/* Card */}
      <div className="bg-card rounded-2xl border border-border p-4 md:p-5">
        {totalActivities === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-2">
            עדיין לא הימרת — לחץ על סדרה פתוחה כדי להתחיל
          </p>
        ) : (
          <>
            {/* User avatar + Points */}
            <div className="flex items-center justify-center gap-3 mb-3">
              <UserAvatar avatarId={avatarId} displayName={displayName} size="md" />
              <div>
                <span className="text-3xl md:text-4xl font-bold text-accent">{totalPoints}</span>
                <span className="text-sm md:text-base text-muted-foreground ml-1">נקודות</span>
              </div>
            </div>

            {/* Stats row */}
            <div className="flex items-center justify-around md:gap-8">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">הימורים</p>
                <p className="text-sm font-semibold">{totalActivities}</p>
              </div>
              <div className="w-px h-6 bg-border" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">כיוון נכון</p>
                <p className="text-sm font-semibold">
                  {totalCompleted > 0 ? `${totalCorrect}/${totalCompleted}` : '—'}
                </p>
              </div>
              <div className="w-px h-6 bg-border" />
              <div className="text-center">
                <p className="text-xs text-muted-foreground">מדויקים</p>
                <p className="text-sm font-semibold">{exactScores}</p>
              </div>
            </div>

          </>
        )}
      </div>
    </motion.div>
  );
}

export default MyBetsSummary;
